
import { observer } from "mobx-react-lite";
import { useSearchParams, useRouter } from "@/lib/router-compat";
import { useState } from "react";
import Link from "@/components/ui/link-compat";
import { useProject, useDeleteProject } from "@/hooks/useProjects";
import { usePageTitle } from '@/hooks/usePageTitle';
import { useDeferredMount } from '@/hooks/useDeferredMount';
import { useProjectDeals } from "@/hooks/useDeals";
import { useTasks } from "@/hooks/useTasks";
import { lazy, Suspense } from "react";
import { ProjectDetailHeader } from "@/components/projects/project-detail-header";
const NoteComposer = lazy(() => import("@/components/notes/note-composer").then(m => ({ default: m.NoteComposer })));
const ProjectDialog = lazy(() => import("@/components/projects/project-dialog").then(m => ({ default: m.ProjectDialog })));
const DealDialog = lazy(() => import("@/components/deals/deal-dialog").then(m => ({ default: m.DealDialog })));
const TaskDialog = lazy(() => import("@/components/tasks/task-dialog").then(m => ({ default: m.TaskDialog })));
import { useEntityTimeline } from "@/hooks/useTimeline";
import { useUpdateNote, useDeleteNote } from "@/hooks/useNotes";
import { useUploadAttachments, useDeleteAttachment } from "@/hooks/useNoteAttachments";
import { useAuth } from "@/contexts/AuthContext";
import { ActivityTimeline } from "@/components/ui/activity-timeline";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Handshake, User, UserCheck, CheckSquare, Pencil, AlignLeft } from "lucide-react";
import { useProjectStore, useUserStore, useContactStore, useTaskStore } from "@/stores/StoreProvider";
import { RelatedTable } from "@/components/ui/related-table";
import { Checkbox } from "@/components/ui/checkbox";
import { CrmTaskStatus } from "@/types/crm";
import { InlineField } from "@/components/ui/inline-field";
import { InlineSelectField } from "@/components/ui/inline-select-field";
import { DealStatus, CrmTaskPriority, ContactType, type CrmTask } from "@/types/crm";
import { getStatusClasses } from "@/lib/status-variants";
import { DetailPageSkeleton } from "@/components/ui/skeletons";
import { DeleteConfirmation } from "@/components/ui/delete-confirmation";

const ProjectDetailPage = observer(function ProjectDetailPage() {
  const bodyReady = useDeferredMount();
  const searchParams = useSearchParams();
  const id = searchParams.get("id") ?? "";
  const { data: project, isLoading } = useProject(id);
  usePageTitle(project?.name ?? 'Project');
  const taskStore = useTaskStore();
  const { data: deals = [] } = useProjectDeals(id);
  const { data: tasks = [] } = useTasks({ projectId: id });
  const { userId } = useAuth();
  const timeline = useEntityTimeline("project", id);
  const timelineEntries = timeline.entries;
  const updateNote = useUpdateNote("project", id);
  const deleteNote = useDeleteNote("project", id);
  const uploadAttachments = useUploadAttachments("project", id);
  const deleteAttachment = useDeleteAttachment("project", id);

  const deleteProject = useDeleteProject();
  const router = useRouter();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDealDialogOpen, setIsDealDialogOpen] = useState(false);
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const projectStore = useProjectStore();
  const userStore = useUserStore();
  const contactStore = useContactStore();
  const saveField = (field: string) => (value: string) => {
    projectStore.update(id, { [field]: value || null });
  };
  const ownerOptions = userStore.all.map((u) => ({
    value: u.id,
    label: u.displayName || u.email,
  }));
  const contactOptions = (() => {
    const all = contactStore.all;
    const companies = all.filter((c) => c.type === ContactType.Company);
    const independentPersons = all.filter((c) => c.type === ContactType.Person && !c.companyId);
    const opts: { value: string; label: string }[] = [];
    for (const company of companies) {
      opts.push({ value: company.id, label: company.name });
      for (const emp of contactStore.getByCompanyId(company.id)) {
        opts.push({ value: emp.id, label: `  └ ${emp.name}` });
      }
    }
    for (const person of independentPersons) {
      opts.push({ value: person.id, label: person.name });
    }
    return opts;
  })();

  if (!bodyReady || isLoading) {
    return <DetailPageSkeleton />;
  }

  if (!project) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground">Project not found</div>;
  }

  return (
    <div className="space-y-6">
      <ProjectDetailHeader project={project} onSaveName={(name) => projectStore.update(id, { name })} onDelete={() => setIsDeleteOpen(true)} />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
        {/* Main content — Notes */}
        {/* Mobile-only bottom inset: the shell's scroll-container padding is not
            honored by Chromium flex scrolling, so clear the fixed bottom nav here. */}
        <div className="space-y-4 min-w-0 order-2 lg:order-1 pb-[calc(var(--mobile-bottom-nav-height)+env(safe-area-inset-bottom))] lg:pb-0">
          <NoteComposer entityType="project" entityId={id} />
          <ActivityTimeline
            entries={timelineEntries}
            isLoading={timeline.isLoading}
            hasMore={timeline.hasMore}
            onLoadMore={() => timeline.loadMore()}
            isLoadingMore={timeline.isLoadingMore}
            currentEntityId={id}
            onEditNote={(noteId, content) => updateNote.mutateAsync({ noteId, content })}
            onDeleteNote={(noteId) => deleteNote.mutateAsync(noteId)}
            onUploadFiles={(noteId, files) => uploadAttachments.mutateAsync({ noteId, files }).then(() => {})}
            onDeleteAttachment={(noteId, attachmentId) => deleteAttachment.mutate({ noteId, attachmentId })}
            currentUserId={userId ?? undefined}
          />
          <Button className="h-11 w-full lg:hidden" onClick={() => setIsEditDialogOpen(true)}>Edit project</Button>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4 order-1 lg:order-2 lg:sticky lg:top-20 lg:self-start">
          {/* Details Card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Details</CardTitle>
                <Button size="sm" variant="ghost" className="h-8 px-2" aria-label="Edit project" onClick={() => setIsEditDialogOpen(true)}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <InlineSelectField
                label="Owner"
                value={project.ownerId ?? null}
                options={ownerOptions}
                onSave={(val) => projectStore.update(id, { ownerId: val || null })}
                icon={<UserCheck className="h-4 w-4" />}
                emptyText="No owner"
                placeholder="Select owner…"
              />
              <InlineSelectField
                label="Contact"
                value={project.contactId ?? null}
                options={contactOptions}
                onSave={(val) => projectStore.update(id, { contactId: val || null })}
                icon={<User className="h-4 w-4" />}
                emptyText="No contact"
                placeholder="Select contact…"
                renderValue={(opt) =>
                  opt ? (
                    <Link
                      href={`/dashboard/contact?id=${opt.value}`}
                      className="text-sm hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {opt.label}
                    </Link>
                  ) : null
                }
              />
              <InlineField
                label="Description"
                value={project.description}
                onSave={saveField("description")}
                multiline
                icon={<AlignLeft className="h-4 w-4" />}
              />
            </CardContent>
          </Card>

          <RelatedTable
            icon={<Handshake className="h-4 w-4" />}
            title="Deals"
            count={deals.length}
            rows={deals}
            getRowKey={(deal) => deal.id}
            onRowClick={(deal) => router.push(`/dashboard/deal?id=${deal.id}`)}
            quickAdd={{ ariaLabel: "Add deal", onClick: () => setIsDealDialogOpen(true) }}
            emptyText="No deals yet."
            columns={[
              { key: "name", render: (deal) => <span className="truncate">{deal.dealTypeName}</span> },
              {
                key: "status",
                render: (deal) => {
                  const statusLabel =
                    deal.status === DealStatus.Won ? "Won" : deal.status === DealStatus.Lost ? "Lost" : "Open";
                  return (
                    <Badge className={`text-xs ${getStatusClasses(statusLabel)}`}>{statusLabel}</Badge>
                  );
                },
              },
            ]}
          />

          <RelatedTable
            icon={<CheckSquare className="h-4 w-4" />}
            title="Tasks"
            count={tasks.length}
            rows={tasks}
            getRowKey={(task) => task.id}
            onRowClick={(task) => router.push(`/dashboard/task?id=${task.id}`)}
            quickAdd={{ ariaLabel: "Add task", onClick: () => setIsTaskDialogOpen(true) }}
            emptyText="No tasks yet."
            columns={[
              {
                key: "done",
                render: (task: CrmTask) => (
                  <span onClick={(event) => event.stopPropagation()} className="inline-flex">
                    <Checkbox
                      checked={task.status === CrmTaskStatus.Completed}
                      aria-label={`Complete ${task.title}`}
                      onCheckedChange={() =>
                        taskStore.update(task.id, { status: CrmTaskStatus.Completed })
                      }
                    />
                  </span>
                ),
              },
              { key: "title", render: (task: CrmTask) => <span className="truncate">{task.title}</span> },
              {
                key: "priority",
                render: (task: CrmTask) => (
                  <Badge
                    variant={
                      task.priority === CrmTaskPriority.High
                        ? "destructive"
                        : task.priority === CrmTaskPriority.Medium
                          ? "default"
                          : "secondary"
                    }
                    className="text-xs"
                  >
                    {task.priority === CrmTaskPriority.High
                      ? "High"
                      : task.priority === CrmTaskPriority.Medium
                        ? "Med"
                        : "Low"}
                  </Badge>
                ),
              },
            ]}
          />
        </aside>
      </div>

      {isEditDialogOpen && <ProjectDialog
        project={project}
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
      />}
      {isDealDialogOpen && <DealDialog deal={null} open={isDealDialogOpen} onClose={() => setIsDealDialogOpen(false)} defaultProjectId={id} />}
      {isTaskDialogOpen && <TaskDialog task={null} open={isTaskDialogOpen} onClose={() => setIsTaskDialogOpen(false)} defaultProjectId={id} />}

      <DeleteConfirmation
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={() => { deleteProject.mutateAsync({ id, name: project.name }); router.push("/dashboard/projects"); }}
        entityType="project"
        entityName={project.name}
      />
    </div>
  );
});


import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/project')({
  component: ProjectDetailPage,
  pendingComponent: () => <DetailPageSkeleton />,
  pendingMs: 0,
  pendingMinMs: 150,
});
